/**
 * Pricing marketing page — ported from `.scratch/bedd-snapshot/pricing.html`
 * into the bimassess SPA. Mirrors `landing.tsx`'s wrapper pattern.
 *
 * Source script: sticky-nav compress, view toggle (Individual / Enterprise
 * pricing sections), scroll reveals, CTA-band spotlight on `.cta-band`. No
 * counters / magnetic buttons / final-CTA on this page.
 */

import { useEffect, useRef } from 'react';
import { markup } from './pricing-assets/markup';
import './marketing-shared/marketing-shared.css';
import './pricing-assets/pricing.css';

export function PricingPage(): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'Pricing · BIMAssess';
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // ─── STICKY NAV COMPRESS ─────────────────────
    const nav = wrapper.querySelector<HTMLElement>('nav.site-nav');
    const onScroll = (): void => {
      if (!nav) return;
      nav.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // ─── SCROLL REVEALS ──────────────────────────
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            revealObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' },
    );
    wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => revealObs.observe(el));

    // ─── VIEW TOGGLE (Individual / Enterprise) ──
    const viewToggle = wrapper.querySelector<HTMLElement>('#view-toggle');
    const sectionIndividual = wrapper.querySelector<HTMLElement>('#section-individual');
    const sectionEnterprise = wrapper.querySelector<HTMLElement>('#section-enterprise');
    const viewBtns = viewToggle
      ? Array.from(viewToggle.querySelectorAll<HTMLButtonElement>('.view-btn'))
      : [];
    const viewHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    viewBtns.forEach((btn) => {
      const handler = (): void => {
        viewBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const section = btn.dataset.section;
        if (sectionIndividual)
          sectionIndividual.style.display = section === 'individual' ? 'block' : 'none';
        if (sectionEnterprise)
          sectionEnterprise.style.display = section === 'enterprise' ? 'block' : 'none';
        // Re-observe any reveals that newly entered the layout.
        wrapper
          .querySelectorAll('.reveal:not(.in-view), .reveal-stagger:not(.in-view)')
          .forEach((el) => revealObs.observe(el));
      };
      btn.addEventListener('click', handler);
      viewHandlers.push({ btn, handler });
    });

    // ─── CTA BAND SPOTLIGHT ──────────────────────
    const ctaBand = wrapper.querySelector<HTMLElement>('.cta-band');
    const ctaMoveHandler = (e: MouseEvent): void => {
      if (!ctaBand) return;
      const rect = ctaBand.getBoundingClientRect();
      ctaBand.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      ctaBand.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    if (ctaBand) ctaBand.addEventListener('mousemove', ctaMoveHandler);

    // ─── REDUCED MOTION ──────────────────────────
    if (typeof window.matchMedia === 'function') {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduced.matches) {
        wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
          el.classList.add('in-view');
        });
      }
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      revealObs.disconnect();
      if (ctaBand) ctaBand.removeEventListener('mousemove', ctaMoveHandler);
      viewHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="bedd-marketing bedd-pricing"
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
